import { v4 as uuid } from 'uuid'
import React, { useCallback, useState, useEffect } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { useSelector, useDispatch } from 'react-redux'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import ShareIcon from '@material-ui/icons/Share'
import Typography from '@material-ui/core/Typography'
import TextField from '@material-ui/core/TextField'

import Button from '@material-ui/core/Button'
import {CopyToClipboard} from 'react-copy-to-clipboard'

import routerSelectors from 'store/selectors/router'
import projectSelectors from 'store/selectors/project'
import projectActions from 'store/modules/project'
import routerActions from 'store/modules/router'
import snackbarActions from 'store/modules/snackbar'

import Window from 'components/dialog/Window'
import Confirm from 'components/dialog/Confirm'
import Item from 'components/project/Item'
import ItemEditor from 'components/project/ItemEditor'
import SettingsEditor from 'components/project/SettingsEditor'
import ColorKey from 'components/project/ColorKey'
import icons from 'icons'

import fileUtils from 'utils/file'

const HomeIcon = icons.home
const SettingsIcon = icons.settings
const AddIcon = icons.add
const ImportExportIcon = icons.importExport

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(5),
  },
  headerTitle: {
    padding: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  headerActions: {
    padding: theme.spacing(1),
  },
  nameInput: {
    width: '100%',
    margin: theme.spacing(1),
  },
  content: {
    padding: theme.spacing(2),
    paddingLeft: theme.spacing(10),
    paddingRight: theme.spacing(10),
  },
  itemList: {
    border: '1px dashed #999',
    padding: theme.spacing(1),
  },
  item: {
    margin: theme.spacing(1),
    padding: theme.spacing(3),
  },
  footer: {
    marginBottom: theme.spacing(1),
  },
  urlContainer: {
    padding: theme.spacing(3),
    backgroundColor: '#000',
    fontFamily: 'Courier',
    color: '#fff',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  }
}))

const Project = ({

}) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const [projectName, setProjectName] = useState('')
  const [editingItem, setEditingItem] = useState(null)
  const [deletingItem, setDeletingItem] = useState(null)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [shareWindowOpen, setShareWindowOpen] = useState(false)

  const loading = useSelector(state => state.network.loading['project/loadProject'])
  const joinWindow = useSelector(projectSelectors.joinWindow)
  const params = useSelector(routerSelectors.params)
  const project = useSelector(projectSelectors.project)

  const items = project ? project.items || [] : []

  const onDragEnd = useCallback(result => {
    if (!result.destination) return
    const startIndex = result.source.index
    const endIndex = result.destination.index
    const newItems = items.concat([])
    const [removed] = newItems.splice(startIndex, 1)
    newItems.splice(endIndex, 0, removed)
    dispatch(projectActions.updateProject({
      id: project.id,
      data: {
        items: newItems,
      },
    }))
  }, [
    items,
  ])

  useEffect(() => {
    if(project) setProjectName(project.name)
  }, [
    project,
  ])

  useEffect(() => {
    dispatch(projectActions.loadProject(params.id))
  }, [
    params.id,
  ])

  useEffect(() => {
    if(!params.id) return
    const unsubscribe = firebase.firestore().collection('project').doc(params.id).onSnapshot((doc) => {
      // this was done locally
      if(doc.metadata.hasPendingWrites) return
      dispatch(projectActions.setProject(doc.data()))
    })
    return () => unsubscribe()
  }, [
    params.id,
  ])

  const updateProjectName = useCallback(() => {
    dispatch(projectActions.updateProject({
      id: project.id,
      data: {
        name: projectName,
      },
    }))
  }, [
    projectName,
    project,
  ])

  const onCloseJoinWindow = useCallback(() => {
    dispatch(projectActions.setJoinWindow(false))
  }, [])

  const onGoHome = useCallback(() => {
    dispatch(routerActions.navigateTo('home'))
  })

  const onAddItem = useCallback(() => {
    setEditingItem({})
  }, [])

  const onEditItem = useCallback((item) => {
    setEditingItem(item)
  })

  const onCloseItemEditor = useCallback(() => {
    setEditingItem(null)
  })

  const onCopyClipboard = useCallback(() => {
    dispatch(snackbarActions.setSuccess('copied to clipboard'))
  }, [])

  const onSaveItemEditor = useCallback((values) => {
    const newItem = Object.assign({}, editingItem, values)
    const newItems = newItem.id ?
      (project.items || []).map(item => {
        return item.id == editingItem.id ?
          newItem :
          item
      }) :
      [Object.assign({}, newItem, {
        id: uuid(),
      })].concat(project.items || [])
    dispatch(projectActions.updateProject({
      id: project.id,
      data: {
        items: newItems,
      },
    }))
    setEditingItem(null)
  }, [
    editingItem,
    project,
  ])

  const onDeleteItem = useCallback((item) => {
    setDeletingItem(item)
  })

  const onConfirmDeleteWindow = useCallback(() => {
    const newItems = (project.items || []).filter(item => item.id != deletingItem.id)
    dispatch(projectActions.updateProject({
      id: project.id,
      data: {
        items: newItems,
      },
    }))
    setDeletingItem(null)
  }, [
    deletingItem,
    project,
  ])

  const onCloseDeleteWindow = useCallback((item) => {
    setDeletingItem(null)
  })

  const onExportProject = useCallback(() => {
    const fileName = project.name
      .replace(/\W+/g, '-')
      .toLowerCase()
    fileUtils.exportFile(project, `${fileName}.json`)
  }, [
    project,
  ])

  const onJoinProject = useCallback(() => {
    dispatch(projectActions.joinProject(project.id))
  }, [
    project,
  ])

  const onOpenSettings = useCallback(() => setSettingsOpen(true))
  const onCloseSettings = useCallback(() => setSettingsOpen(false))
  const onSubmitSettings = useCallback((settings) => {
    dispatch(projectActions.updateProject({
      id: project.id,
      data: {
        settings,
      },
    }))
    setSettingsOpen(false)
  })

  if(!project) {
    return (
      <div>
        <Typography>
          { loading ? 'Loading...' : 'Project not found' }
        </Typography>
      </div>
    )
  }

  return (
    <div className={ classes.root }>
      <div className={ classes.headerActions }>
        <Button
          size="small"
          variant="contained"
          onClick={ onGoHome }
        >
          Home&nbsp;<HomeIcon />
        </Button>
        &nbsp;&nbsp;&nbsp;
        <Button
          size="small"
          variant="contained"
          onClick={ onExportProject }
        >
          Export&nbsp;<ImportExportIcon />
        </Button>
        &nbsp;&nbsp;&nbsp;
        <Button
          size="small"
          variant="contained"
          onClick={ () => setShareWindowOpen(true) }
        >
          Share&nbsp;<ShareIcon />
        </Button>
        &nbsp;&nbsp;&nbsp;
        <Button
          size="small"
          variant="contained"
          onClick={ onOpenSettings }
        >
          Settings&nbsp;<SettingsIcon />
        </Button>
        &nbsp;&nbsp;&nbsp;
        <Button
          size="small"
          color="primary"
          variant="contained"
          onClick={ onAddItem }
        >
          Add Item&nbsp;<AddIcon />
        </Button>
      </div>
      <div className={ classes.headerTitle }>
        <TextField
          id="project-name"
          label="Project Title"
          value={ projectName }
          fullWidth
          onChange={ (e) => setProjectName(e.target.value) }
          onBlur={ updateProjectName }
        />
      </div>
      <div className={ classes.content }>
        <DragDropContext onDragEnd={ onDragEnd }>
          <Droppable droppableId="droppable">
            {(provided, snapshot) => (
              <div
                className={ classes.itemList }
                { ...provided.droppableProps }
                ref={ provided.innerRef }
              >
                {
                  items.map((item, i) => {
                    return (
                      <Draggable key={ item.id } draggableId={ item.id } index={ i }>
                        {(provided, snapshot) => (
                          <div
                            ref={ provided.innerRef }
                            { ...provided.draggableProps }
                            { ...provided.dragHandleProps }
                            style={ provided.draggableProps.style }
                          >
                            <Item
                              key={ i }
                              item={ item }
                              onEdit={ onEditItem }
                              onDelete={ onDeleteItem }
                            />
                          </div>
                        )}

                      </Draggable>
                    )
                  })
                }
                { provided.placeholder }
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>
      {
        project && project.settings && (
          <div className={ classes.footer }>
            <ColorKey settings={ project.settings } />
            <a href="#" onClick={ onOpenSettings }>
              Edit these settings
            </a>
          </div>
        )
      }
      <div className={ classes.footer }>
        <Typography variant="caption">
          You can click and drag the order of the items in the list.
        </Typography>
      </div>
      {
        editingItem && (
          <ItemEditor
            item={ editingItem }
            settings={ project.settings }
            onClose={ onCloseItemEditor }
            onSubmit={ onSaveItemEditor }
          />
        )
      }
      {
        deletingItem && (
          <Confirm
            title={`Delete Item?`}
            onClose={ onCloseDeleteWindow }
            onSubmit={ onConfirmDeleteWindow }
          >
            <Typography>
              Are you absolutely sure you want to delete this item?
            </Typography>
          </Confirm>
        )
      }
      {
        settingsOpen && (
          <SettingsEditor
            initialValues={ project.settings }
            onClose={ onCloseSettings }
            onSubmit={ onSubmitSettings }
          />
        )
      }
      {
        shareWindowOpen && (
          <Window
            open
            title="Share URL"
            size="md"
            withCancel
            onCancel={ () => setShareWindowOpen(false) }
          >
            <Typography gutterBottom>
              Send this URL (via email or messenger) to other people and they can join this strategy board
              by clicking to open it in a browser:
            </Typography>
            <div className={ classes.urlContainer }>
              { window.location.toString() }
            </div>
            <CopyToClipboard
              text={ window.location.toString() }
              onCopy={ onCopyClipboard }
            >
              <Button
                variant="outlined"
                color="secondary"
              >
                Copy to clipboard
              </Button>
            </CopyToClipboard>
          </Window>
        )
      }
      {
        joinWindow && (
          <Window
            open
            title="Join Project"
            size="sm"
            withCancel
            onCancel={ onCloseJoinWindow }
          >
            <Typography gutterBottom>
              You are not currently part of this project - click "Join" and it will appear on your home screen.
            </Typography>
            <Button
              variant="outlined"
              color="secondary"
              onClick={ onJoinProject }
            >
              Join Project
            </Button>
          </Window>
        )
      }
    </div>
  )
}

export default Project